import { uid } from "../../helpers/product";
import { FormState, Product, ProductData } from "../../types/product";

// ─── All Products ─────────────────────────────────────────────────────────────

export const PAGE_SIZE_OPTIONS = [10, 20, 50];

export const CATEGORIES = [
  "All",
  "Carbohydrase",
  "Hydrolase",
  "Glycosidase",
  "Lipase",
  "Protease",
  "General",
];

// ─── Manage Product ───────────────────────────────────────────────────────────

export const MOCK_PRODUCT: ProductData = {
  product_description:
    "High-activity glucoamylase for starch saccharification in bioethanol and glucose syrup production. Delivers 99%+ starch hydrolysis with exceptional thermostability up to 65°C.",
  meta_title: "Aurel Glucoamylase | Industrial Enzyme for Starch Conversion",
  meta_description:
    "Premium glucoamylase enzyme for bioethanol, glucose syrup and food processing. 100,000 U/mL activity, food grade certified. Request a sample today.",
  metaTitle: "Aurel Glucoamylase | Industrial Enzyme for Starch Conversion",
  metaDescription:
    "Premium glucoamylase enzyme for bioethanol, glucose syrup and food processing. 100,000 U/mL activity, food grade certified. Request a sample today.",
  status: "draft",
  product_specifications: [
    { key: "pH Optimum", value: "4.0 – 5.0" },
    { key: "Temperature", value: "55 – 65 °C" },
    { key: "Activity", value: "100,000 U/mL" },
    { key: "Purity", value: "> 99%" },
    { key: "Min. Order", value: "25 kg" },
  ],
  product_key: "glucoamylase",
  tab_name: "Glucoamylase",
  tab_sub: "Starch · Bioethanol",
  ec: "Carbohydrase · EC 3.2.1.3",
  name: ["Gluco", "amylase"],
  name_em_start: 5,
  tagline: "Turns starch into fermentable sugars — the critical enzyme driving a $12 billion global market spanning bioethanol, glucose syrup, and high-fructose corn syrup production.",
  formats: ["Liquid Concentrate", "Lyophilised Powder"],
  certs: ["Food Grade", "Industrial Grade", "CoA Included"],
  cta_copy: "Available in liquid and powder formats for different application methods. Our technical team provides complete process optimisation and effluent reduction strategies.",
  applications: [
    {
      kicker: "Application — Bioethanol",
      heading: ["More ethanol.", "Same feedstock."],
      headingEm: 1,
      body: "The difference between a profitable distillery and a marginal one is conversion efficiency. Glucoamylase hydrolyses starch dextrins into fermentable glucose — giving your yeast exactly what it needs to push ethanol yield higher. With global bioethanol production reaching 29 billion gallons annually and feedstock costs representing 70% of operating expenses, every percentage point of improved conversion directly impacts your bottom line.",
      transformFrom: {
        label: "Input",
        text: "Grain or cassava starch — unfermentable without saccharification",
      },
      transformTo: {
        label: "Output",
        text: "High-glucose wort — ready for fermentation at maximum yield",
      },
      metrics: [
        { num: "15%", label: "Higher ethanol yield" },
        { num: "99.2%", label: "Starch conversion" },
        { num: "35%", label: "Faster saccharification" },
        { num: "$2.40", label: "Cost per additional gallon" },
      ],
      buyersLabel: "Who buys this",
      buyers: ["Ethanol plants", "Distilleries", "Fuel blending units"],
      flowLabel: "Process in your plant",
      flowSteps: [
        {
          title: "Feedstock preparation",
          desc: "Grain or starch milled and slurried with water at 30–35% DS",
        },
        {
          title: "Liquefaction",
          desc: "Alpha-amylase at 85–90°C breaks chains into short dextrins",
        },
        {
          title: "Glucoamylase saccharification",
          desc: "55–65°C, pH 4.0–5.0 — dextrins converted to glucose over 48–72 hrs",
        },
        {
          title: "Fermentation",
          desc: "Glucose-rich wort pitched with yeast — ethanol at maximum conversion",
        },
      ],
      flowResultText: "Up to 12% more ethanol per tonne of feedstock",
    }
  ],
  focusKeyword: "glucoamylase",
  secondaryKeywords: ["bioethanol", "starch conversion"],
  faqs: [
    {
      question: "What is the shelf life of Aurel Glucoamylase?",
      answer: "Under recommended storage conditions (4-8°C), our enzyme retains >95% activity for 12 months."
    }
  ],
};

export const EMPTY_FORM = (): FormState => ({
  product_description: "",
  meta_title: "",
  meta_description: "",
  metaTitle: "",
  metaDescription: "",
  status: "draft",
  product_specifications: [{ id: uid(), key: "", value: "" }],
  product_key: "",
  tab_name: "",
  tab_sub: "",
  ec: "",
  name: ["", ""],
  name_em_start: 0,
  tagline: "",
  formats: [],
  certs: [],
  cta_copy: "",
  applications: [
    {
      id: uid(),
      kicker: "Application — Name",
      heading: ["", ""],
      headingEm: 1,
      body: "",
      transformFrom: { label: "Input", text: "" },
      transformTo: { label: "Output", text: "" },
      metrics: [{ num: "", label: "" }],
      buyersLabel: "Who buys this",
      buyers: [],
      flowLabel: "Process steps",
      flowSteps: [{ title: "", desc: "" }],
      flowResultText: "",
    }
  ],
  focusKeyword: "",
  secondaryKeywords: [],
  faqs: [],
  tds_url: "",
});

export const inputBase: React.CSSProperties = {
  width: "100%",
  background: "var(--input-bg)",
  border: "1px solid var(--border)",
  borderRadius: 8,
  padding: "10px 14px",
  fontSize: 14,
  color: "var(--text)",
  outline: "none",
  fontFamily: "'DM Sans', sans-serif",
  transition: "border-color 0.15s, box-shadow 0.15s",
  boxSizing: "border-box",
};
